/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  // CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  FetchStationProperties,
  RequestStationInterface,
  StationFilter,
  StationInitialProperties,
} from '../../../interface/partner-interfaces/location-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import station from '../../../utils/api/partner/station';

// a thunk to fetch stations
export const fetchPendingStations = createAsyncThunk<
  FetchStationProperties,
  RequestStationInterface
>(
  'pending-location-management/fetchPendingStations',
  async (data: RequestStationInterface, { dispatch }) => {
    try {
      const response = await station.fetchStations(
        removeEmptyKeys({ ...data, submissionStatus: 'pending' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const refreshPendingStations = createAsyncThunk<
  FetchStationProperties,
  RequestStationInterface
>('pending-location-management/refreshPendingStations', async (data: RequestStationInterface, { dispatch }) => {
  try {
    const response = await station.fetchStations(
      removeEmptyKeys({ ...data, submissionStatus: 'pending' })
    );
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Corrected pending notification thunk
export const fetchPendingNotification = createAsyncThunk<number>(
  'unapproved-location-management/fetchPendingNotification',
  async (_, { dispatch }) => {
    try {
      const response = await station.fetchPendingNotificationCount();
      return response.data.pendingCount;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'pending-location-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await station.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: StationInitialProperties = {
  stations: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  pendingNotificationCount: 0,
  totalResults: 0,
  partnerStations: [],
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const pendingStationSlice = createSlice({
  name: 'pending-location-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setPendingStationFilterData: (
      state: StationInitialProperties,
      action: PayloadAction<StationFilter>,
    ) => {
      state.filter = action.payload;
    },

    /**
     * Update approved station management
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetPendingStationToInitialState: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },

  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingStations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingStations.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.stations = action.payload.results?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          amenities: stationData?.amenities || [],
          opensAt: ConvertUTCtoHHMM(stationData?.opensAt),
          closesAt: ConvertUTCtoHHMM(stationData?.closesAt),
        }));
      })
      .addCase(fetchPendingStations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(refreshPendingStations.fulfilled, (state, action) => {
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.stations = action.payload.results?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          amenities: stationData?.amenities || [],
          opensAt: ConvertUTCtoHHMM(stationData?.opensAt),
          closesAt: ConvertUTCtoHHMM(stationData?.closesAt),
        }));
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
    builder
      .addCase(fetchPendingNotification.pending, (state: StationInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchPendingNotification.fulfilled,
        (state: StationInitialProperties, action: PayloadAction<number>) => {
          state.loading = false;
          state.pendingNotificationCount = action.payload;
        },
      )
      .addCase(fetchPendingNotification.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export the actions
export const {
  setPendingStationFilterData,
  resetPendingStationToInitialState,
  updateStationFilters,
  setSortData,
} = pendingStationSlice.actions;

// Export the reducer
export default pendingStationSlice.reducer;

export const setPendingLocationFilterData = setPendingStationFilterData;

export const updateLocationFilters = updateStationFilters;
